import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PlatformService } from 'app/core/platform/platform.service';
import { UserService } from 'app/core/user/user.service';
import { Session, User, UserRole } from 'app/core/user/user.types';
import { SignUpDTO } from 'app/modules/partner/user-management/user-management.types';
import { ReleaseSubscription } from 'app/modules/user/payments/payments.types';
import { Release, Store } from 'app/modules/user/releases/releases.types';
import { UtilService } from 'app/shared/util.service';
import { getHttpParamsFromObj } from 'app/utils';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class UserManagementService {
    private _user: BehaviorSubject<User | null> = new BehaviorSubject(null);
    private _users: BehaviorSubject<User[] | null> = new BehaviorSubject(null);

    constructor(
        private _httpClient: HttpClient,
        private _utilService: UtilService,
        private _platformService: PlatformService,
        private _userService: UserService,
    ) {
    }

    get user$(): Observable<User> {
        return this._user.asObservable();
    }

    get users$(): Observable<User[]> {
        return this._users.asObservable();
    }

    getUsers(searchQuery?: string, partnerUuid?: string, roles?: string[], statistics?: boolean): Observable<User[]> {
        let params = getHttpParamsFromObj({ search: searchQuery, partner: partnerUuid, roles, statistics })

        return this._httpClient.get<User[]>('api/user/all', {
            params: params
        }).pipe(
            tap((users) => {
                users = users.sort((a, b) => a.personalInformation?.lastName > b.personalInformation?.lastName ? 1 : -1);
                for (let user of users) {
                    if (user.role === "admin") {
                        let de = this._utilService.getCountryFromAlpha2("de");
                        if (user.personalInformation) {
                            user.personalInformation.country = de;
                        } else {
                            user.personalInformation = {
                                country: de
                            } as any;
                        }
                    }
                }
                this._users.next(users);
            })
        );
    }

    getActiveUserCount(): Observable<{role: string, c: string}[]> {
        return this._httpClient.get<{role: string, c: string}[]>('api/user/active-user-count');
    }

    getUserById(id: string): Observable<User> {
        if (this._user.value?.uuid === id) {
            return this._user.asObservable().pipe(filter(user => user !== null));
        }
        if (this._users.value) {
            let user = this._users.value.find(item => item.uuid === id) || null;
            if (user) {
                this._user.next(user);
                return of(user);
            }
        }
        return this._httpClient.get<User>('api/user/by-uuid/' + id).pipe(
            tap((user) => {
                this._user.next(user);
            }
            )
        );
    }

    updateUserStores(user: any): Observable<User> {
        let body = {
            storeBlacklist: user.storeBlacklist,
        };
        if (user.storeCommission) {
            body['storeCommission'] = user.storeCommission;
        }
        return this.users$.pipe(
            take(1),
            switchMap(users => this._httpClient.post<User>('api/user/by-uuid/' + user.uuid + '/stores', body).pipe(
                map((updatedUser) => {
                    const index = users.findIndex(item => item.uuid === user.uuid);
                    users[index] = updatedUser;
                    this._users.next(users);
                    return updatedUser;
                }),
                switchMap(updatedUser => this.user$.pipe(
                    take(1),
                    filter(item => item && item.uuid === user.uuid),
                    tap(() => {
                        this._user.next(updatedUser);
                        return updatedUser;
                    })
                ))
            ))
        );
    }

    updateUser(id: string, user: any): Observable<User> {
        return this.users$.pipe(
            take(1),
            switchMap(users => this._httpClient.patch<User>('api/user/by-uuid/' + id, {
                personalInformation: user.personalInformation,
                notes: user.notes,
                billing: user.billingSettings,
                storeBlacklist: user.storeBlacklist,
                permissions: user.permissions,
                royaltiesDistributionProfile: user.royaltiesDistributionProfile,
            }).pipe(
                map((updatedUser) => {
                    const index = users.findIndex(item => item.uuid === id);
                    users[index] = updatedUser;
                    this._users.next(users);
                    return updatedUser;
                }),
                switchMap(updatedUser => this.user$.pipe(
                    take(1),
                    filter(item => item && item.uuid === id),
                    tap(() => {
                        this._user.next(updatedUser);
                        return updatedUser;
                    })
                ))
            ))
        );
    }

    deleteUser(id: string): Observable<void> {
        return this.users$.pipe(
            take(1),
            switchMap(users => this._httpClient.delete('api/user/by-uuid/' + id).pipe(
                map(() => {
                    const index = users.findIndex(item => item.uuid === id);
                    users.splice(index, 1);
                    this._users.next(users);
                })
            ))
        );
    }

    createUser(dto: SignUpDTO, partnerUuid?: string): Observable<User> {
        return this.users$.pipe(
            take(1),
            switchMap(users => this._httpClient.post<User>('api/auth/create-user', dto, {
                params: getHttpParamsFromObj({ partner: partnerUuid })
            }).pipe(
                map((createdUser) => {
                    users.push(createdUser);
                    users = users.sort((a, b) => a.personalInformation?.lastName > b.personalInformation?.lastName ? 1 : -1);
                    this._users.next(users);
                    return createdUser;
                })
            ))
        );
    }

    assignUserRole(id: string, role: UserRole) {
        return this.users$.pipe(
            take(1),
            switchMap(users => this._httpClient.post<User>('api/user/by-uuid/' + id + "/role", { role: role })
                .pipe(
                    map((updatedUser) => {
                        const index = users.findIndex(item => item.uuid === id);
                        users[index] = updatedUser;
                        this._users.next(users);
                        return updatedUser;
                    }),
                    switchMap(updatedUser => this.user$.pipe(
                        take(1),
                        filter(item => item && item.uuid === id),
                        tap(() => {

                            // Update the user if it's selected
                            this._user.next(updatedUser);

                            // Return the updated user
                            return updatedUser;
                        })
                    ))
                ))
        );
    }

    clearSessions(user: User) {
        return this._httpClient.post<void>('api/user/by-uuid/' + user.uuid + '/clear-sessions', {});

    }

    clearCurrentUser() {
        this._user.next(null);
    }

    getUserName(user: User): string {
        return `${user?.personalInformation?.firstName} ${user?.personalInformation?.lastName}`;
    }

    getUserCompany(user: User): string {
        return user?.personalInformation?.company;
    }

    getUserAvatar(user: User): string {
        return this._platformService.getFileSrc(user?.personalInformation?.avatar, 'avatar');
    }

    getUserStoreBlacklist(user: User): Observable<Store[]> {
        return this._httpClient.get<Store[]>('api/user/by-uuid/' + user.uuid + '/stores');
    }

    getReleaseNames(releases?: Release[]) {
        if (!releases) {
            return '';
        }
        return releases.map(release => release.title).join(', ');
    }

    getSubscriptionNames(subscriptions?: ReleaseSubscription[]) {
        if (!subscriptions) {
            return '';
        }
        return subscriptions.map(subscription => subscription.title).join(', ');
    }

    getLastSessions(userId: string) {
        return this._httpClient.get<Session[]>('api/user/by-uuid/' + userId + '/sessions').pipe(map(sessions => {
            for (let session of sessions) {
                session.ipAddress = session.ipAddress.replace("::ffff:", "");
                if (session.ccIso) {
                    session.country = this._utilService.getCountryFromAlpha2(session.ccIso);
                }
            }
            return sessions;
        }));
    }
}
